import { DocumentationRules } from '@agroone/business'
import {
  Campaign,
  ConstantLite,
  DocumentedApp,
  Languages,
  Length,
  UserDto,
  UserPreferenceFormRaw,
} from '@agroone/entities'

import {
  DestroyRef,
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core'
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { RouterModule } from '@angular/router'
import { BndBadge, BndButton, BndLoading, BndSelect, BndSwitch, BndTextInput } from '@bonduelle-it/storybook'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { SharedConstantService } from '../../services/shared-constant.service'
import { requireDefined } from '../../utils/require-defined.util'
import { radiusValidator } from '../../validators/radius.validator'
import { UserPreferencesSelectOption } from './interfaces/user-preferences-select-option.interface'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

/**
 * Presentational user-preferences form shared across apps.
 *
 * It only renders and validates the preferences form itself: the surrounding
 * chrome (header bar, navigation…) is left to each host app since designs
 * differ. Data that depends on the host app (campaigns, environment version,
 * logout route…) is provided through inputs, and side effects (save, cache
 * clearing) are delegated to the host through outputs.
 */
@Component({
  selector: 'user-preferences-form',
  templateUrl: './user-preferences-form.component.html',
  styleUrls: ['./user-preferences-form.component.scss'],
  imports: [
    ReactiveFormsModule,
    RouterModule,
    TranslatePipe,
    MatIconModule,
    BndBadge,
    BndButton,
    BndLoading,
    BndSelect,
    BndTextInput,
    BndSwitch,
  ],
})
export class UserPreferencesFormComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef)

  public readonly user = input.required<UserDto>()
  public readonly allCampaigns = input<Campaign[]>([])
  public readonly version = input('')
  public readonly apiDocUrl = input('')
  /** API serving the functional documentation, with `documentedApp` naming the host app's page. */
  public readonly apiUrl = input('')
  public readonly documentedApp = input<DocumentedApp>()
  public readonly logoutRoute = input('')
  public readonly showClearCache = input(true)
  public readonly pending = input(false)

  public readonly save = output<UserPreferenceFormRaw>()
  public readonly clearCache = output<void>()

  public readonly optionLabelKey = 'label'
  public readonly optionValueKey = 'value'

  public readonly preferencesForm = signal<FormGroup | null>(null)
  public readonly languages = signal<UserPreferencesSelectOption[]>([])
  public readonly geolocationRadiusUnitOptions = signal<UserPreferencesSelectOption[]>([])
  public readonly availableRegionOptions = signal<UserPreferencesSelectOption[]>([])
  public readonly defaultYieldUnits = signal<ConstantLite[]>([])
  public readonly defaultAreaUnits = signal<ConstantLite[]>([])
  public readonly defaultApplicationUnits = signal<ConstantLite[]>([])
  public readonly campaigns = signal<Campaign[]>([])
  public readonly maxRadius = signal(0)

  /** Link to the host app's functional documentation, empty when the user cannot open that app. */
  public readonly functionalDocUrl = computed(() => {
    const app: DocumentedApp | undefined = this.documentedApp()

    if (!app || !this.apiUrl() || !DocumentationRules.canRead(this.user(), app)) {
      return ''
    }

    return `${this.apiUrl()}/app-docs/${app}/index.html`
  })

  private readonly MAX_MILES = 100
  private readonly MAX_KM = 161
  private readonly KM_TO_MILES_RATIO = 1.61

  private readonly formBuilder: FormBuilder = inject(FormBuilder)
  private readonly translateService: TranslateService = inject(TranslateService)
  private readonly constantService: SharedConstantService = inject(SharedConstantService)

  constructor() {
    effect(() => {
      this.allCampaigns() // track signal
      untracked(() => {
        if (this.preferencesForm()) {
          this._setCampaignsForRegion(this.region?.value)
        }
      })
    })
  }

  private get form(): FormGroup {
    return this.preferencesForm()!
  }

  private get geolocationRadius(): AbstractControl {
    return this.form.controls['geolocationRadius']
  }

  private get geolocationRadiusUnit(): AbstractControl {
    return this.form.controls['geolocationRadiusUnit']
  }

  private get region(): AbstractControl {
    return this.form.controls['region']
  }

  private get campaignId(): AbstractControl {
    return this.form.controls['campaignId']
  }

  private get displayCloseCrops(): AbstractControl {
    return this.form.controls['displayCloseCrops']
  }

  ngOnInit(): void {
    const currentUser: UserDto = this.user()
    this.maxRadius.set(currentUser.geolocationRadiusUnit === Length.KM ? this.MAX_KM : this.MAX_MILES)
    this._loadOptions()
    this._initForm()

    this.availableRegionOptions.set(
      [...(currentUser.availableRegions ?? [])].sort().map((regionName) => ({
        label: regionName,
        value: regionName,
      })),
    )
    this._setCampaignsForRegion(currentUser.regionName)

    this._observeValueChanges()
  }

  public onClearCache(): void {
    this.clearCache.emit()
  }

  public onSave(): void {
    this.save.emit(this.form.value)
  }

  private _initForm(): void {
    const currentUser: UserDto = this.user()
    const form: FormGroup = this.formBuilder.group(
      {
        language: [currentUser.language, Validators.required],
        defaultYieldUnit: [currentUser.defaultYieldUnit, Validators.required],
        defaultAreaUnit: [currentUser.defaultAreaUnit, Validators.required],
        defaultApplicationUnit: [currentUser.defaultApplicationUnit, Validators.required],
        geolocationRadius: [currentUser.geolocationRadius || 1, Validators.required],
        geolocationRadiusUnit: [currentUser.geolocationRadiusUnit],
        region: [currentUser.regionName],
        limitDisplay: [currentUser.limitDisplay ?? false],
        campaignId: [currentUser.campaignId],
        displayCloseCrops: [currentUser?.displayCloseCrops || false],
      },
      { validators: [radiusValidator] },
    )
    this.preferencesForm.set(form)

    this._updateCampaignValidators(this.displayCloseCrops.value)

    this.displayCloseCrops.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this._updateCampaignValidators(value)
    })
  }

  private _loadOptions(): void {
    const languages: UserPreferencesSelectOption[] = []
    for (const [key, value] of Object.entries(Languages)) {
      languages.push({
        value,
        label: this.translateService.instant(`LANGUAGE.${key}`),
      })
    }
    languages.sort((x, y) => x.label.localeCompare(y.label))
    this.languages.set(languages)

    const geolocationRadiusUnitOptions: UserPreferencesSelectOption[] = []
    for (const value of Object.values(Length)) {
      geolocationRadiusUnitOptions.push({ label: value, value })
    }
    this.geolocationRadiusUnitOptions.set(geolocationRadiusUnitOptions)

    this._setUnitsForRegion(this.user().regionName)
  }

  private _observeValueChanges(): void {
    this.geolocationRadiusUnit.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((unit) => {
      const radius = this.geolocationRadius.value
      switch (unit) {
        case Length.MILES:
          this.geolocationRadius.setValue(Math.round(radius / this.KM_TO_MILES_RATIO))
          this.maxRadius.set(this.MAX_MILES)
          break
        case Length.KM:
          this.geolocationRadius.setValue(Math.round(radius * this.KM_TO_MILES_RATIO))
          this.maxRadius.set(this.MAX_KM)
          break
        default:
          this.maxRadius.set(this.MAX_MILES)
      }
    })

    this.region.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((regionValue) => {
      this._setUnitsForRegion(regionValue)
      this._setCampaignsForRegion(regionValue)
      if (!this.campaigns().find((v) => v.id === this.campaignId.value)) {
        this.campaignId.setValue(null)
      }
    })
  }

  private _setUnitsForRegion(regionName: string): void {
    this.defaultYieldUnits.set(this.constantService.getFiltered(this.constantService.yieldUnit, undefined, regionName))
    this.defaultAreaUnits.set(this.constantService.getFiltered(this.constantService.areaUnit, undefined, regionName))
    this.defaultApplicationUnits.set(
      this.constantService.getFiltered(this.constantService.nutrientsQuantityUnit, undefined, regionName),
    )
  }

  private _setCampaignsForRegion(regionName: string): void {
    this.campaigns.set(
      this.allCampaigns().filter((v) =>
        requireDefined(v.regions, 'the campaign regions').find((r) => r.name === regionName),
      ),
    )
    // Re-set the value so BndSelect re-renders its label against the freshly bound campaigns.
    // Likely redundant now: BndSelect.items is a signal input read by the displayLabels computed(),
    // which already recomputes when the list changes.
    const current = this.campaignId?.value
    if (current != null) {
      this.campaignId.setValue(current, { emitEvent: false })
    }
  }

  private _updateCampaignValidators(isDisplayCloseCropsOn: boolean): void {
    if (isDisplayCloseCropsOn) {
      this.campaignId.addValidators(Validators.required)
    } else {
      this.campaignId.clearValidators()
    }
    this.campaignId.updateValueAndValidity()
  }
}
